import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "status"]
  static values = { url: String, debounceMs: Number }

  connect() {
    this._timer = null
    this._abort = null
    this._lastSaved = this.hasInputTarget ? (this.inputTarget.value || "").toString() : ""
    this._statusTimer = null
    this._setState("idle")

    this._boundOnActivity = () => this._scheduleSave()

    // Milkdown rich editor (ProseMirror) mounts into this target.
    const rich = this.element.querySelector('[data-milkdown-target="editor"]')
    // Milkdown markdown mode mounts into this target.
    const md = this.element.querySelector('[data-milkdown-target="markdownEditor"]')

    ;[rich, md].filter(Boolean).forEach((el) => {
      el.addEventListener("input", this._boundOnActivity, true)
      el.addEventListener("keyup", this._boundOnActivity, true)
      el.addEventListener("blur", this._boundOnActivity, true)
    })
  }

  disconnect() {
    if (this._timer) {
      clearTimeout(this._timer)
      this._timer = null
    }
    if (this._abort) {
      try { this._abort.abort() } catch (_) {}
      this._abort = null
    }

    const rich = this.element.querySelector('[data-milkdown-target="editor"]')
    const md = this.element.querySelector('[data-milkdown-target="markdownEditor"]')
    ;[rich, md].filter(Boolean).forEach((el) => {
      el.removeEventListener("input", this._boundOnActivity, true)
      el.removeEventListener("keyup", this._boundOnActivity, true)
      el.removeEventListener("blur", this._boundOnActivity, true)
    })
  }

  _scheduleSave() {
    if (!this.hasUrlValue || !this.urlValue) return
    if (!this.hasInputTarget) return

    const debounce = this.hasDebounceMsValue ? this.debounceMsValue : 750
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      this._timer = null
      this._saveNow()
    }, debounce)
  }

  async _saveNow() {
    if (!this.hasUrlValue || !this.urlValue) return
    if (!this.hasInputTarget) return

    const body = (this.inputTarget.value || "").toString()
    if (body === this._lastSaved) return

    this._setState("saving")

    if (this._abort) {
      try { this._abort.abort() } catch (_) {}
    }
    this._abort = new AbortController()

    try {
      const res = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({ body }),
        signal: this._abort.signal,
      })

      const contentType = (res.headers.get("content-type") || "").toString()
      const data = contentType.includes("application/json")
        ? await res.json().catch(() => ({}))
        : null

      if (!res.ok) {
        const msg = (data && data.error) ? data.error : `HTTP ${res.status}`
        throw new Error(msg)
      }
      if (!data) {
        throw new Error(`Unexpected response (HTTP ${res.status})`)
      }

      this._lastSaved = body
      this._setState("saved")
      this._clearSoon()
    } catch (e) {
      // Ignore aborts (superseded saves).
      if (e?.name === "AbortError") return
      this._setState("error", e?.message || "failed")
    }
  }

  _csrfToken() {
    return document
      .querySelector("meta[name='csrf-token']")
      ?.getAttribute("content")
      ?.toString() || ""
  }

  _setState(state, errorMessage) {
    if (!this.hasStatusTarget) return

    const current = (state || "idle").toString()
    const root = this.statusTarget

    const savingIcon = root.querySelector('[data-note-status="saving"]')
    const savedIcon = root.querySelector('[data-note-status="saved"]')
    const errorIcon = root.querySelector('[data-note-status="error"]')
    const sr = root.querySelector('[data-note-status="sr"]')

    const showSaving = current === "saving"
    const showSaved = current === "saved"
    const showError = current === "error"
    const visible = showSaving || showSaved || showError

    root.classList.toggle("d-none", !visible)

    if (savingIcon) savingIcon.classList.toggle("d-none", !showSaving)
    if (savedIcon) savedIcon.classList.toggle("d-none", !showSaved)
    if (errorIcon) errorIcon.classList.toggle("d-none", !showError)

    if (sr) {
      sr.textContent =
        showSaving ? "Saving" :
        showSaved ? "Saved" :
        showError ? "Error saving" :
        ""
    }

    if (showError) {
      root.title = `Error saving: ${(errorMessage || "").toString()}`
    } else {
      root.title = ""
    }

    try {
      this.element.toggleAttribute("aria-busy", showSaving)
    } catch (_) {
      // ignore
    }
  }

  _clearSoon() {
    clearTimeout(this._statusTimer)
    this._statusTimer = setTimeout(() => {
      this._setState("idle")
    }, 900)
  }
}
